import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, MailIcon, MessageSquareTextIcon, TagIcon, UserRoundIcon, XIcon } from 'lucide-react';
import type { FanSignup } from '../../data/fanSignups';
import { Button } from '../ui/Button';
interface FanDetailDrawerProps {
  fan: FanSignup | null;
  onClose: () => void;
  onSave: (id: string, changes: Pick<FanSignup, 'tags' | 'notes'>) => boolean;
}
export function FanDetailDrawer({
  fan,
  onClose,
  onSave
}: FanDetailDrawerProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!fan) return;
    setTags(fan.tags);
    setNotes(fan.notes);
    setTagInput('');
    setSaved(false);
  }, [fan]);
  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || tags.some(item => item.toLowerCase() === tag.toLowerCase())) return;
    setTags(current => [...current, tag]);
    setTagInput('');
  };
  const save = () => {
    if (!fan || !onSave(fan.id, {
      tags,
      notes
    })) return;
    setSaved(true);
  };
  return <AnimatePresence data-id="element-362" data-mp="35h536i02w3d3">
      {fan && <>
        <motion.button aria-label="Close fan profile" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" data-id="element-363" data-mp="35gd590j0dtjn" />
        <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} aria-label={`Fan profile for ${fan.firstName}`} className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[440px] flex-col border-l border-white/[0.08] bg-ink-900" data-id="element-364" data-mp="2vh4f6ir9kz2k">
          <header className="flex items-start justify-between border-b border-white/[0.06] p-5" data-id="element-365" data-mp="2riouwmmki4su">
            <div data-id="element-366" data-mp="kojqegnj1svy"><p className="text-[11px] font-medium uppercase tracking-[0.14em] text-accent-soft" data-id="element-367" data-mp="kojmw6objar7">Fan profile</p><h2 className="mt-1 text-[20px] font-semibold tracking-tight text-white" data-id="element-368" data-mp="pn4r0h6oyekx">{fan.firstName || 'Unnamed fan'}</h2><p className="mt-1 text-[12px] text-zinc-500" data-id="element-369" data-mp="j98nprkzag5r">Joined {formatDate(fan.createdAt)}</p></div>
            <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-white" data-id="element-370" data-mp="e9w0v6u3x936"><XIcon className="h-5 w-5" data-id="element-371" data-mp="9cm6dcrv278h" /></button>
          </header>

          <div className="flex-1 space-y-6 overflow-y-auto p-5" data-id="element-372" data-mp="16z31qch1aarl">
            <section data-id="element-373" data-mp="11zqa7ef7wfio">
              <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wider text-zinc-500" data-id="element-374" data-mp="11zgkzt1zyce1">Contact</h3>
              <div className="space-y-3 rounded-2xl border border-white/[0.06] bg-ink-850 p-4" data-id="element-375" data-mp="x7yj92s5g7s0">
                <DetailRow icon={UserRoundIcon} label="Name" value={fan.firstName || 'Not provided'} data-id="element-376" data-mp="saoulpcl4058" />
                <DetailRow icon={MailIcon} label="Email" value={fan.email} data-id="element-377" data-mp="1pqcjta4ve7us" />
                <DetailRow icon={MessageSquareTextIcon} label="Mobile" value={fan.phone || 'Not provided'} data-id="element-378" data-mp="1ky2n9b9929jj" />
              </div>
            </section>

            <section data-id="element-379" data-mp="1sbk0rru28uvp">
              <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wider text-zinc-500" data-id="element-380" data-mp="1sc3jvbvg5pbb">Consent & source</h3>
              <div className="grid grid-cols-2 gap-2.5" data-id="element-381" data-mp="2286iw8v057u1">
                <InfoCard label="Entry point" value={fan.source === 'website-vip' ? 'Website VIP' : 'Backstage'} data-id="element-382" data-mp="226lpwnoc5wya" />
                <InfoCard label="SMS consent" value={fan.smsOptIn ? 'Opted in' : 'Not opted in'} accent={fan.smsOptIn} data-id="element-383" data-mp="274n7f4645o7n" />
                <InfoCard label="Email status" value="Captured" accent data-id="element-384" data-mp="2g7ellbqdiy2z" />
                <InfoCard label="Last updated" value={formatDate(fan.updatedAt)} data-id="element-385" data-mp="2g5lc7oodlzra" />
              </div>
              {fan.captureSourceId && <div className="mt-2.5 rounded-xl border border-white/[0.06] bg-ink-850 px-3 py-2.5" data-id="element-386" data-mp="2px7vm8avb554"><div className="text-[10px] text-zinc-500" data-id="element-387" data-mp="2pzc28y05ii93">Capture campaign</div><div className="mt-1 break-all text-[12px] font-medium text-accent-soft" data-id="element-388" data-mp="2uy5fcpbj078q">{fan.captureSourceId}</div></div>}
              <p className="mt-3 text-[11px] leading-relaxed text-zinc-500" data-id="element-389" data-mp="2yw35pvh18zdp">CRM delivery is not connected yet. Do not text this fan unless the SMS consent status is opted in.</p>
            </section>

            <section data-id="element-390" data-mp="3rlv23nl1o7q9">
              <h3 className="mb-3 flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wider text-zinc-500" data-id="element-391" data-mp="99q7qebh5ycn"><TagIcon className="h-3.5 w-3.5" data-id="element-392" data-mp="57uvies0a0k3" /> Internal tags</h3>
              <div className="rounded-2xl border border-white/[0.06] bg-ink-850 p-4" data-id="element-393" data-mp="5aqtg33usp8j">
                <div className="flex flex-wrap gap-1.5" data-id="element-394" data-mp="3dog5nywdizos">{tags.length ? tags.map(tag => <button key={tag} type="button" onClick={() => setTags(current => current.filter(item => item !== tag))} title={`Remove ${tag}`} className="rounded-full bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-accent-soft transition-colors hover:bg-danger/15 hover:text-danger" data-id="element-395" data-mp="38qeq3ny5pii2">{tag} ×</button>) : <span className="text-[12px] text-zinc-600" data-id="element-396" data-mp="11xc9ri520uxe">No internal tags yet.</span>}</div>
                <div className="mt-3 flex gap-2" data-id="element-397" data-mp="wyix1rxld7ya"><input value={tagInput} onChange={event => setTagInput(event.target.value)} onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addTag();
                  }
                }} placeholder="Add a tag" className="min-w-0 flex-1 rounded-lg border border-white/[0.1] bg-ink-800 px-3 py-2 text-[12px] text-white outline-none placeholder:text-zinc-600 focus:border-accent/70" data-id="element-398" data-mp="wzanypg8uhi8" /><Button type="button" size="sm" variant="secondary" onClick={addTag} data-id="element-399" data-mp="10ij9k7frgsnk">Add</Button></div>
              </div>
            </section>

            <section data-id="element-400" data-mp="1o9ez0xwqvbrn">
              <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wider text-zinc-500" data-id="element-401" data-mp="1t5vxakxnr6ig">Internal notes</h3>
              <textarea value={notes} onChange={event => {
              setNotes(event.target.value);
              setSaved(false);
            }} rows={5} placeholder="Record context for the team. These notes are not shown to fans." className="w-full resize-y rounded-xl border border-white/[0.1] bg-ink-850 px-3 py-2.5 text-[13px] leading-relaxed text-white outline-none placeholder:text-zinc-600 focus:border-accent/70 focus:ring-2 focus:ring-accent/15" data-id="element-402" data-mp="1ei1658zata7h" />
            </section>
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-white/[0.06] p-4" data-id="element-403" data-mp="2lq9itxxpqwvh"><span className="text-[11px] text-grow" data-id="element-404" data-mp="2grg09r3kkfog">{saved && <span className="inline-flex items-center gap-1" data-id="element-405" data-mp="2btob34ub77ny"><CheckIcon className="h-3.5 w-3.5" data-id="element-406" data-mp="2bwiuct1l6mfs" /> Saved to CRM</span>}</span><Button variant="primary" size="sm" onClick={save} data-id="element-407" data-mp="27s2r6zjcxt49">Save profile</Button></footer>
        </motion.aside>
      </>}
    </AnimatePresence>;
}
function DetailRow({
  icon: Icon,
  label,
  value
}: {
  icon: React.ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-3 text-[13px]" data-id="element-408" data-mp="1t4zuuw6rtqii"><Icon className="h-4 w-4 shrink-0 text-zinc-500" data-id="element-409" data-mp="1t1lolcuzkx5s" /><span className="w-14 shrink-0 text-zinc-500" data-id="element-410" data-mp="1o5wnh53bj87d">{label}</span><span className="min-w-0 break-all text-zinc-200" data-id="element-411" data-mp="1k3a13jpf2fhk">{value}</span></div>;
}
function InfoCard({
  label,
  value,
  accent = false
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return <div className="rounded-xl border border-white/[0.06] bg-ink-850 px-3 py-2.5" data-id="element-412" data-mp="1f6jeedj3wln6"><div className="text-[10px] text-zinc-500" data-id="element-413" data-mp="3qr935jsmka90">{label}</div><div className={`mt-1 text-[12px] font-medium ${accent ? 'text-grow' : 'text-zinc-200'}`} data-id="element-414" data-mp="3vpu3rkbsgvxz">{value}</div></div>;
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}